import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Users,
  UserCheck,
  Clock,
  UserX,
  Filter,
  Sparkles
} from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { matchingService } from '@/services/matchingService'
import { useAuth } from '@/contexts/Auth0Context'
import { useToast } from '@/contexts/ToastContext'
import {
  Relationship,
  MatchingRequest,
  RelationshipStatus,
  MatchingRequestStatus,
  SuggestedMentee
} from '@/types/matching'
import MenteeRequestCard from '@/components/matching/MenteeRequestCard'
import RelationshipCard from '@/components/matching/RelationshipCard'
import SuggestedMenteeCard from '@/components/matching/SuggestedMenteeCard'
import RateRelationshipModal from '@/components/matching/RateRelationshipModal'

type FilterType = 'all' | 'pending' | 'active' | 'completed' | 'suggested'

const MenteesPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { showToast } = useToast()

  const [relationships, setRelationships] = useState<Relationship[]>([])
  const [pendingRequests, setPendingRequests] = useState<MatchingRequest[]>([])
  const [suggestedMentees, setSuggestedMentees] = useState<SuggestedMentee[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('all')
  const [processingRequestId, setProcessingRequestId] = useState<string | null>(null)
  const [processingAction, setProcessingAction] = useState<'accept' | 'decline' | null>(null)

  // Rating modal state
  const [ratingRelationship, setRatingRelationship] = useState<Relationship | null>(null)
  const [isRatingSubmitting, setIsRatingSubmitting] = useState(false)

  // Prevent duplicate API calls
  const fetchingDataRef = useRef(false)

  const fetchData = useCallback(async () => {
    if (fetchingDataRef.current) return

    try {
      fetchingDataRef.current = true
      setLoading(true)

      // Fetch relationships, pending requests and suggested mentees in parallel
      const [relationshipsData, requestsData, suggestedData] = await Promise.all([
        matchingService.getMyRelationships(),
        matchingService.getPendingRequests().catch(() => []),
        matchingService.getSuggestedMentees().catch(() => [])
      ])

      setRelationships(Array.isArray(relationshipsData) ? relationshipsData : [])
      setPendingRequests(Array.isArray(requestsData) ? requestsData : [])
      setSuggestedMentees(Array.isArray(suggestedData) ? suggestedData : [])
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch data'
      })
    } finally {
      setLoading(false)
      fetchingDataRef.current = false
    }
  }, [showToast])

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      if (!authLoading && !isAuthenticated) {
        setLoading(false)
      }
      return
    }

    fetchData()
  }, [authLoading, isAuthenticated, fetchData])

  const handleAcceptRequest = async (requestId: string) => {
    try {
      setProcessingRequestId(requestId)
      setProcessingAction('accept')
      await matchingService.acceptMentee(requestId)
      showToast({
        type: 'success',
        message: 'Mentee request accepted!'
      })
      fetchData()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to accept request'
      })
    } finally {
      setProcessingRequestId(null)
      setProcessingAction(null)
    }
  }

  const handleDeclineRequest = async (requestId: string) => {
    try {
      setProcessingRequestId(requestId)
      setProcessingAction('decline')
      await matchingService.declineMentee(requestId)
      showToast({
        type: 'success',
        message: 'Mentee request declined'
      })
      fetchData()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to decline request'
      })
    } finally {
      setProcessingRequestId(null)
      setProcessingAction(null)
    }
  }

  const handleSubmitRating = async (rating: number, review?: string) => {
    if (!ratingRelationship) return
    const wasActive = ratingRelationship.status === RelationshipStatus.ACTIVE

    try {
      setIsRatingSubmitting(true)
      await matchingService.rateRelationship(ratingRelationship.id, { rating, review })

      if (wasActive) {
        try {
          await matchingService.completeRelationship(ratingRelationship.id)
          showToast({ type: 'success', message: 'Mentorship ended and rating submitted!' })
        } catch (completeError) {
          if ((completeError as { code?: string })?.code === 'RATINGS_INCOMPLETE') {
            showToast({
              type: 'success',
              message: "Rating submitted! Waiting on your mentee to finish up."
            })
          } else {
            throw completeError
          }
        }
      } else {
        showToast({ type: 'success', message: 'Rating submitted!' })
      }

      setRatingRelationship(null)
      fetchData()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to submit rating'
      })
    } finally {
      setIsRatingSubmitting(false)
    }
  }

  const handleEndMentorship = (relationship: Relationship) => {
    setRatingRelationship(relationship)
  }

  // Filter relationships
  const activeRelationships = relationships.filter(
    (r) => r.status === RelationshipStatus.ACTIVE
  )
  const completedRelationships = relationships.filter(
    (r) => r.status === RelationshipStatus.COMPLETED
  )

  // Filter pending requests
  const allPendingRequests = pendingRequests.filter(
    (r) => r.status === MatchingRequestStatus.PENDING
  )

  // Stats
  const stats = {
    activeMentees: activeRelationships.length,
    pendingRequests: allPendingRequests.length,
    completedRelationships: completedRelationships.length,
    totalMentees: activeRelationships.length + completedRelationships.length
  }

  // Filter content based on selected filter
  const getFilteredContent = () => {
    switch (selectedFilter) {
      case 'pending':
        return { showPending: true, showActive: false, showCompleted: false, showSuggested: false }
      case 'active':
        return { showPending: false, showActive: true, showCompleted: false, showSuggested: false }
      case 'completed':
        return { showPending: false, showActive: false, showCompleted: true, showSuggested: false }
      case 'suggested':
        return { showPending: false, showActive: false, showCompleted: false, showSuggested: true }
      default:
        return { showPending: true, showActive: true, showCompleted: false, showSuggested: false }
    }
  }

  const { showPending, showActive, showCompleted, showSuggested } = getFilteredContent()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Your Mentees</h1>
          <p className="mt-2 text-gray-600">
            Manage mentee requests and your active mentorship relationships
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Mentees</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.activeMentees}
                  </p>
                </div>
                <UserCheck className="w-8 h-8 text-green-400" />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending Requests</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {stats.pendingRequests}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-accent-700">
                    {stats.completedRelationships}
                  </p>
                </div>
                <UserX className="w-8 h-8 text-accent-400" />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Mentees</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalMentees}
                  </p>
                </div>
                <Users className="w-8 h-8 text-gray-400" />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <button
              onClick={() => setSelectedFilter('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedFilter === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedFilter('pending')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedFilter === 'pending'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Pending Requests
              {stats.pendingRequests > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                  {stats.pendingRequests}
                </span>
              )}
            </button>
            <button
              onClick={() => setSelectedFilter('active')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedFilter === 'active'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Active Mentees
            </button>
            <button
              onClick={() => setSelectedFilter('completed')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedFilter === 'completed'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Completed
              {stats.completedRelationships > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full text-xs">
                  {stats.completedRelationships}
                </span>
              )}
            </button>
            <button
              onClick={() => setSelectedFilter('suggested')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedFilter === 'suggested'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Suggested
            </button>
          </div>
        </div>

        {/* Pending Requests Section */}
        {showPending && allPendingRequests.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Pending Requests
            </h2>
            <div className="space-y-4">
              {allPendingRequests.map((request) => (
                <MenteeRequestCard
                  key={request.id}
                  request={request}
                  onAccept={handleAcceptRequest}
                  onDecline={handleDeclineRequest}
                  isProcessing={processingRequestId === request.id}
                  processingAction={processingRequestId === request.id ? processingAction : null}
                />
              ))}
            </div>
          </div>
        )}

        {/* Active Mentees Section */}
        {showActive && activeRelationships.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Active Mentees
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {activeRelationships.map((relationship) => (
                <RelationshipCard
                  key={relationship.id}
                  relationship={relationship}
                  userRole="mentor"
                  onEndMentorship={handleEndMentorship}
                />
              ))}
            </div>
          </div>
        )}

        {/* Completed Relationships Section */}
        {showCompleted && completedRelationships.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Completed Mentorships
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {completedRelationships.map((relationship) => (
                <RelationshipCard
                  key={relationship.id}
                  relationship={relationship}
                  userRole="mentor"
                  onRate={setRatingRelationship}
                />
              ))}
            </div>
          </div>
        )}

        {showCompleted && completedRelationships.length === 0 && (
          <div className="text-center py-12">
            <UserX className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <Card className="max-w-md mx-auto">
              <CardBody>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Completed Mentorships
                </h3>
                <p className="text-gray-600">
                  Mentorships you've completed with mentees will show up here.
                </p>
              </CardBody>
            </Card>
          </div>
        )}

        {/* Suggested Mentees Section */}
        {showSuggested && suggestedMentees.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Suggested Mentees
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {suggestedMentees.map((mentee) => (
                <SuggestedMenteeCard key={mentee.id} mentee={mentee} />
              ))}
            </div>
          </div>
        )}

        {showSuggested && suggestedMentees.length === 0 && (
          <div className="text-center py-12">
            <Sparkles className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <Card className="max-w-md mx-auto">
              <CardBody>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Suggested Mentees
                </h3>
                <p className="text-gray-600">
                  We don't have any suggested mentees for you right now. Check
                  back once more mentees join the platform.
                </p>
              </CardBody>
            </Card>
          </div>
        )}

        {/* Empty State */}
        {selectedFilter === 'all' && allPendingRequests.length === 0 && activeRelationships.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <Card className="max-w-md mx-auto">
              <CardBody>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Mentees Yet
                </h3>
                <p className="text-gray-600">
                  You don't have any mentee requests or active mentorships yet.
                  Make sure your profile is complete and you're marked as available
                  for mentoring!
                </p>
              </CardBody>
            </Card>
          </div>
        )}

        {/* Empty state for filtered views */}
        {selectedFilter === 'pending' && allPendingRequests.length === 0 && (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <Card className="max-w-md mx-auto">
              <CardBody>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Pending Requests
                </h3>
                <p className="text-gray-600">
                  You don't have any pending mentee requests at the moment.
                </p>
              </CardBody>
            </Card>
          </div>
        )}

        {selectedFilter === 'active' && activeRelationships.length === 0 && (
          <div className="text-center py-12">
            <UserCheck className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <Card className="max-w-md mx-auto">
              <CardBody>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Active Mentees
                </h3>
                <p className="text-gray-600">
                  You don't have any active mentees yet. Accept a pending request
                  to start mentoring!
                </p>
              </CardBody>
            </Card>
          </div>
        )}

        {/* Rate Relationship Modal */}
        {ratingRelationship && (
          <RateRelationshipModal
            relationship={ratingRelationship}
            userRole="mentor"
            isOpen={!!ratingRelationship}
            onClose={() => setRatingRelationship(null)}
            onSubmit={handleSubmitRating}
            isSubmitting={isRatingSubmitting}
          />
        )}
      </div>
    </div>
  )
}

export default MenteesPage
